import React, { useState, useRef, useEffect } from "react";
import MainLayout from "../Common/MainLayout";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Toast } from "primereact/toast";
import "./CriteriaManagement.css";
import httpService from "../../services/httpService";
import { fetchAuthSession } from "aws-amplify/auth";
import { useLocation } from "react-router-dom";

const EditHierarchy = () => {
  const location = useLocation();
  const { criteriaId, criteriaName } = location.state || {};
  let [providerTypeData, setProviderTypeData] = useState([]);

  let [specialtyData, setSpecialtyData] = useState([]);

  const [isDialogVisible, setIsDialogVisible] = useState(false);
  const [selectedProviderType, setSelectedProviderType] = useState(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [hierarchy, setHierarchyData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const toast = useRef(null);

  const fetchHierarchyData = async () => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken;
    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "ProviderDetails",
          id_criteria: criteriaId,
        },
        token
      )
      .then((response) => {
        const HierarchyData = response.data.Hierarchies.map((item) => ({
          rowId: item.criteriatypeid,
          criteriaId: item.criteriaid,
          providerCode: item.providerCode,
          providerType: item.provider,
          primarySpecialtyCode: item.cde_speclty_prim,
          primarySpecialty: item.type_speclty_prim,
          is_deleted: item.isdeleted,
        }));
        setHierarchyData(HierarchyData);
      })
      .catch((error) => {
        console.error("Error fetching Hierarchy details:", error);
      });
  };

  const fetchInitialData = async () => {
    setLoading(true);
    await fetchHierarchyData();

    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "GetProviders",
        },
        token
      )
      .then((response) => {
        const providerTypeData = response.data.map((item) => ({
          providerType: item.type_provr,
          providerCode: item.cde_type_provr,
        }));
        setProviderTypeData(providerTypeData);
      })
      .catch((error) => {
        console.error("Error fetching provider types:", error);
      });

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "GetPrimarySpecialties",
        },
        token
      )
      .then((response) => {
        const specialtyData = response.data.map((item) => ({
          specialty: item.type_speclty_prim,
          specialtyCode: item.cde_speclty_prim,
        }));
        setSpecialtyData(specialtyData);
      })
      .catch((error) => {
        console.error("Error fetching specialties:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const onDelete = async (rowData) => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "DeleteHierarchy",
          criteria_id: criteriaId,
          idn_criteria_type: rowData.rowId,
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        toast.current.show({
          severity: "warn",
          summary: "Success",
          detail: "Provider Hierarchy has been deleted successfully.",
          life: 3000,
        });
        fetchHierarchyData();
      })
      .catch((error) => {
        console.error("Error deleting Provider Hierarchy:", error);
        toast.current.show({
          severity: "error",
          summary: "Failure",
          detail: "Error deleting Provider Hierarchy. Please try again.",
          life: 3000,
        });
      });
  };

  const confirmDelete = (rowData) => {
    confirmDialog({
      message: "Do you wish to delete the selected provider hierarchy?",
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      accept: () => onDelete(rowData),
      reject: () => {},
    });
  };

  const deleteBodyTemplate = (rowData) => {
    return (
      <Button
        label="Delete Provider Hierarchy"
        icon="pi pi-trash"
        className="p-button-danger"
        onClick={() => confirmDelete(rowData)}
      />
    );
  };

  const handleAdd = () => {
    setIsDialogVisible(true);
  };

  const handleDialogSave = async () => {
    if (!selectedProviderType && !selectedSpecialty) {
      toast.current.show({
        severity: "warn",
        summary: "Validation Error",
        detail:
          "Please select at least one dropdown option (Provider Type or Specialty).",
        life: 3000,
      });
      return;
    }

    const session = await fetchAuthSession();
    const token = session.tokens.idToken;

    httpService
      .post(
        process.env.REACT_APP_CRITERIA_MGMT_LAMBDA_PATH,
        {
          operationType: "BulkUpdateHierarchy",
          criteria_id: criteriaId,
          idn_criteria_type: 0,
          cde_type_provr: selectedProviderType?.providerCode,
          cde_speclty_prim: selectedSpecialty?.specialtyCode,
        },
        token
      )
      .then((response) => {
        console.log(response.data);
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Provider Hierarchy has been added successfully.",
          life: 3000,
        });
        setSelectedProviderType(null);
        setSelectedSpecialty(null);
        setIsDialogVisible(false);
        fetchHierarchyData();
      })
      .catch((error) => {
        console.error("Error creating criteria:", error);
        toast.current.show({
          severity: "error",
          summary: "Failure",
          detail: "Error creating provider hierarchy. Please try again.",
          life: 3000,
        });
      });
  };

  const handleDialogCancel = () => {
    setSelectedProviderType(null);
    setSelectedSpecialty(null);
    setIsDialogVisible(false);
  };

  return (
    <MainLayout>
      <Toast ref={toast} position="top-right" />
      <div className="edit-minimum-providers">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            margin: "1rem 0rem",
          }}
        >
          <Card
            title={`Edit Provider Hierarchy - ${criteriaName.toUpperCase()}`.toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
            style={{ border: "none", boxShadow: "none" }}
          />
          <Button
            label="Add"
            icon="pi pi-plus"
            className="p-button-primary"
            size="small"
            onClick={handleAdd}
          />
        </div>
        <DataTable
          value={hierarchy.filter((item) => !item.is_deleted)}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 15]}
          loading={isLoading}
        >
          <Column
            field="providerType"
            header="Provider Type"
            align={"center"}
            alignHeader={"center"}
            style={{ width: "25%" }}
          ></Column>
          <Column
            field="primarySpecialty"
            header="Specialty"
            align={"center"}
            alignHeader={"center"}
            style={{ width: "25%" }}
          ></Column>
          <Column
            header="Delete"
            body={deleteBodyTemplate}
            align={"center"}
            alignHeader={"center"}
            style={{ width: "15%" }}
          ></Column>
        </DataTable>
        <ConfirmDialog />

        <Dialog
          header="Add Provider Type"
          visible={isDialogVisible}
          style={{ width: "30vw" }}
          onHide={handleDialogCancel}
          footer={
            <div>
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={handleDialogCancel}
              />
              <Button
                label="Save"
                icon="pi pi-check"
                className="p-button-primary"
                onClick={handleDialogSave}
              />
            </div>
          }
        >
          <div className="p-field">
            <label htmlFor="providerType">Provider Type</label>
            <Dropdown
              id="providerType"
              value={selectedProviderType}
              options={providerTypeData}
              onChange={(e) => setSelectedProviderType(e.value)}
              optionLabel="providerType"
              placeholder="Select a Provider Type"
              showClear
              style={{ width: "100%" }}
            />
          </div>
          <div className="p-field" style={{ marginTop: "1rem" }}>
            <label htmlFor="specialty">Specialty</label>
            <Dropdown
              id="specialty"
              value={selectedSpecialty}
              options={specialtyData}
              onChange={(e) => setSelectedSpecialty(e.value)}
              optionLabel="specialty"
              placeholder="Select a Specialty"
              showClear
              style={{ width: "100%" }}
            />
          </div>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default EditHierarchy;
